import { DragHandleIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import InfoIconComponent from 'components/Icons/InfoIcon';
import {
  getAllInstrumentOutputs,
  getAllInstrumentOutputsStatus,
  getAllInstruments,
  setAllInstruments,
  setCalculationLocadingStatus,
  setEditInstrumentData,
  setEditInstrumentOutput,
  updateInstrument,
  getTimeFilter,
  getCompareDashboardScenario,
  getSelectedDashboardScenario,
  getReportingAnchorPeriod,
  handleSelectedRow,
} from 'redux/DashboardSliceV2';
import { AppDispatch } from 'utils/GlobalHelpers';
import Spinner from 'components/Spinner';
import { STATUS } from 'projectConstants';
import {
  type ReportingWindow,
  formatInstrumentBadge,
  formatInstrumentBadgeShort,
} from 'utils/reportingPeriodUtils';
import { getBlocks } from 'redux/ModelsSlice';
import InstrumentErrorBoundary from 'components/ErrorBoundary/InstrumentErrorBoundary';

import EditableTitle from './EditableTitle';
import KpiCard from './kpi/KpiCard';
import ChartCard from './chart/ChartCard';
import DashboardTableWrapper from './table/DashboardTableWrapper';
import VarianceWrapper from './variance/VarianceWrapper';

export interface InstrumentProperties {
  instrumentData: any;
  deleteInstrument: (instrumentData: any) => void;
}
// Width thresholds (px) for badge display
const BADGE_SHORT_THRESHOLD = 300; // below this: show short label only
const BADGE_HIDE_THRESHOLD = 160; // below this: hide badge entirely

const Instrument = ({ instrumentData, deleteInstrument }: InstrumentProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const allInstrumentOutputsStatus = useSelector(getAllInstrumentOutputsStatus);
  const [hover, setHover] = useState<boolean>(false);
  const [cardWidth, setCardWidth] = useState<number>(9999);
  const cardReference = useRef<HTMLDivElement>(null);
  const allInstruments: Array<any> = useSelector(getAllInstruments);
  const allInstrumentsData = useSelector(getAllInstrumentOutputs);
  const timeFilter = useSelector(getTimeFilter);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const anchorPeriod = useSelector(getReportingAnchorPeriod);
  const model = useSelector(getBlocks);
  const fyStartMonth: string =
    (model?.time_properties?.fy_start_month as string | undefined) ?? 'Jan';

  // Track the instrument card's actual pixel width via ResizeObserver
  useEffect(() => {
    const element = cardReference.current;
    const observer = new ResizeObserver(([entry]) => {
      setCardWidth(entry.contentRect.width);
    });
    if (element) observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Reporting window override badge (shown when the instrument deviates from the global window)
  const windowMode: string | undefined = instrumentData?.formatting?.reporting_window_mode;
  const windowOverride: ReportingWindow | undefined =
    instrumentData?.formatting?.reporting_window_override;
  const hasWindowOverride = windowMode === 'OVERRIDE' && !!windowOverride;
  // Choose label based on card width: full → short → hidden
  const overrideBadgeLabel = hasWindowOverride
    ? cardWidth < BADGE_HIDE_THRESHOLD
      ? null
      : cardWidth < BADGE_SHORT_THRESHOLD
      ? formatInstrumentBadgeShort(anchorPeriod, windowOverride!, fyStartMonth)
      : formatInstrumentBadge(anchorPeriod, windowOverride!, fyStartMonth)
    : null;

  const handleEditClick = () => {
    dispatch(setCalculationLocadingStatus(STATUS.IDLE));
    // Clear any row selection from a previously-edited instrument so the
    // formatting panel doesn't bleed state across instruments (same indicator_id
    // can exist in multiple variance instruments on the same dashboard).
    dispatch(handleSelectedRow(null));
    dispatch(setEditInstrumentData(instrumentData));
    dispatch(setEditInstrumentOutput(allInstrumentsData?.[instrumentData?.id]));
  };

  const handleTitleChange = (title: any) => {
    const editData = JSON.parse(JSON.stringify(instrumentData));
    editData.name = title;
    if (editData.id) {
      const foundIndex = allInstruments.findIndex(
        (instrument: any) => instrument.id === editData.id,
      );
      if (foundIndex > -1) {
        const list = JSON.parse(JSON.stringify(allInstruments));
        list[foundIndex] = editData;
        dispatch(setAllInstruments([...list]));
      }
      const compareScenarioId = compareDashboardScenario?.id || null;
      dispatch(
        updateInstrument({
          request_body: {
            ...editData,
            save: true,
            data_values: false,
            start_date: timeFilter?.start || '',
            end_date: timeFilter?.end || '',
            compare_scenario: compareScenarioId,
            scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
          },
        }),
      );
    }
  };

  const buildVarianceOutput = (outputData: any): any => {
    const data = allInstrumentsData[outputData?.id]?.data ?? [];
    const returnValue: any = {
      ...outputData,
      data: data.map((item: any, index: number) => {
        const metaIndicator = outputData.instrument_indicators?.[index] ?? {};
        const fromItem = item?.indicator ?? {};
        const indicatorName = fromItem.indicator_name ?? metaIndicator.indicator_name;
        const indicatorId =
          metaIndicator.indicator_id ?? fromItem.indicator_id ?? item?.indicator_id;
        const indicator = {
          ...metaIndicator,
          ...fromItem,
          indicator_id: indicatorId,
          indicator_name: indicatorName,
        };
        return { ...item, indicator };
      }),
    };

    return returnValue;
  };

  return (
    <>
      {/* @ts-ignore - Chakra Flex can infer a union type too complex to represent in some TS configs */}
      <Flex
        ref={cardReference}
        height={'100%'}
        padding={'0.6rem'}
        zIndex={'9999'}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        width='100%'
        flexDirection='column'
      >
        <Flex justifyContent={'space-between'} flexShrink={0}>
          {/* Title + badge together on the left; title sizes to content, badge follows inline */}
          <Flex flex='1' minW='0' alignItems={'center'} gap={2}>
            {/*
              No flex='1' — Box is content-width so the badge sits right after the text.
              sx overrides EditableTitle's inner Text minH which otherwise creates dead
              vertical space below the text, making the badge appear too low.
            */}
            <Box minW='0' sx={{ p: { minH: 'unset' } }}>
              <EditableTitle
                value={instrumentData?.name}
                handleChange={handleTitleChange}
                isEditable={true}
                width='100%'
              />
            </Box>
            {/* Badge scales with header text (em = % of this Flex's font-size) */}
            {overrideBadgeLabel && (
              <Badge
                colorScheme='purple'
                fontSize='10px'
                px={1.5}
                py={0.5}
                borderRadius='4px'
                whiteSpace='nowrap'
                flexShrink={0}
                alignSelf='center'
              >
                {overrideBadgeLabel}
              </Badge>
            )}
          </Flex>
          <Flex
            gap={1}
            visibility={hover ? 'visible' : 'hidden'}
            alignItems={'start'}
            flexShrink={0}
          >
            <Menu
              closeOnSelect={true}
              strategy='fixed'
              variant={'primary'}
              direction={'ltr'}
              placement='left-start'
            >
              <MenuButton>
                <InfoIconComponent />
              </MenuButton>
              <MenuList>
                <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                <MenuItem onClick={deleteInstrument}>Delete</MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>
        {allInstrumentOutputsStatus !== STATUS.SUCCESS && (
          <Flex
            flex='1'
            minH='0'
            direction='column'
            alignItems='center'
            justifyContent='center'
            justify='center'
            align='center'
            w={'full'}
          >
            <Spinner size='xl' />
          </Flex>
        )}
        {allInstrumentOutputsStatus === STATUS.SUCCESS && (
          <Box flex='1' minH='0' position={'relative'} className='scroll' overflow={'auto'}>
            {/* Show a friendly error state when the backend reported a problem for this instrument */}
            {allInstrumentsData?.[instrumentData?.id]?.error === true ? (
              <Flex
                height='100%'
                alignItems='center'
                justifyContent='center'
                flexDirection='column'
                gap={1}
                px={3}
              >
                <Tooltip
                  label={allInstrumentsData[instrumentData.id]?.message || 'An error occurred'}
                  placement='top'
                >
                  <Text
                    fontSize='sm'
                    color='red.400'
                    textAlign='center'
                    cursor='default'
                    userSelect='none'
                  >
                    ⚠ Something went wrong
                  </Text>
                </Tooltip>
              </Flex>
            ) : (
              <>
                {instrumentData?.instrument_type === 'kpi' && (
                  <InstrumentErrorBoundary>
                    <KpiCard widgetData={instrumentData} isDashboardLayout={true} />
                  </InstrumentErrorBoundary>
                )}
                {(instrumentData.instrument_type === 'line' ||
                  instrumentData.instrument_type === 'bar' ||
                  instrumentData.instrument_type === 'stack') && (
                  <InstrumentErrorBoundary>
                    <ChartCard chartData={instrumentData} isDashboardLayout={true} />
                  </InstrumentErrorBoundary>
                )}
                {instrumentData?.instrument_type === 'table' && (
                  <InstrumentErrorBoundary>
                    <DashboardTableWrapper widget={instrumentData} isDashboardLayout={true} />
                  </InstrumentErrorBoundary>
                )}
                {instrumentData?.instrument_type === 'variance' && (
                  <InstrumentErrorBoundary>
                    <VarianceWrapper
                      output={buildVarianceOutput(instrumentData)}
                      columnSettings={instrumentData?.formatting?.columns_settings}
                      columnPositions={instrumentData?.formatting?.columns_position}
                    />
                  </InstrumentErrorBoundary>
                )}
              </>
            )}
          </Box>
        )}

        {hover && (
          <Box position='absolute' bottom='3' right='3' zIndex='dropdown'>
            <button className='dashboard-drag-header'>
              <DragHandleIcon />
            </button>
          </Box>
        )}
      </Flex>
    </>
  );
};

export default Instrument;
